import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { ScoreRing } from '../components/GradeRing.js';
import { scanError, useStartScan } from '../components/site.js';
import { Button, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';

type AiVisibility = Awaited<ReturnType<typeof loadAiVisibility>>;
type Status = AiVisibility['checks'][number]['status'];
type Bot = AiVisibility['bots'][number];

const loadAiVisibility = (siteId: string) =>
  unwrap(api.GET('/sites/{siteId}/ai-visibility', { params: { path: { siteId } } }));

/** Colour of a check (sheet: .badge); what cannot be checked is neutral. */
const TONE: Record<Status, string> = { ok: 'ok', warn: 'warn', bad: 'bad', unknown: 'neutral' };
/** Text of a check per status: what is fine, what could be better, what is wrong. */
const TEXT = { ok: 'ok', warn: 'warn', bad: 'why' } as const;

/**
 * The caps of the score in packages/rules (aiReadiness): noindex or every search robot blocked
 * keeps it at 20 – the site cannot be named at all –, some blocked search robots at 60.
 */
export const AI_CAP = { shut: 20, blocked: 60 } as const;

/** Why the score cannot rise above a cap, from the answer of the API (audit: caps of the code). */
function capOf(data: AiVisibility): { key: 'noindex' | 'shut' | 'blocked'; cap: number } | null {
  if (data.checks.some((c) => c.key === 'indexable' && c.status === 'bad'))
    return { key: 'noindex', cap: AI_CAP.shut };
  const search = data.bots.filter((b) => b.purpose === 'search');
  if (search.length > 0 && search.every((b) => b.allowed === false))
    return { key: 'shut', cap: AI_CAP.shut };
  return data.blockedSearch.length > 0 ? { key: 'blocked', cap: AI_CAP.blocked } : null;
}

export const verdictOf = (score: number) =>
  score >= 80 ? 'good' : score >= 50 ? 'medium' : 'weak';

/** A robot as allowed, blocked or unknown; blocking a training robot is the owner's choice. */
function botTone(bot: Bot) {
  if (bot.allowed === null) return 'neutral';
  if (bot.allowed) return 'ok';
  return bot.purpose === 'training' ? 'neutral' : 'bad';
}

/**
 * Readiness for AI search (T-42, site-ai.html): can ChatGPT, Perplexity, Copilot and Claude read
 * and cite the site? The score, what we checked, the robots of robots.txt and how we check.
 */
export function AiSearch() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const checksId = useId();
  const botsId = useId();
  const howId = useId();
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'seo_home'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'seo_home', limit: 1 } },
        }),
      ),
    refetchInterval: (query) => {
      const s = query.state.data?.items[0]?.status;
      return s === 'queued' || s === 'running' ? 5000 : false;
    },
  });
  const latestScan = scans.data?.items[0];
  const running = latestScan?.status === 'queued' || latestScan?.status === 'running';
  const query = useQuery({
    queryKey: ['site', siteId, 'ai-visibility', latestScan?.status],
    queryFn: () => loadAiVisibility(siteId),
  });
  const start = useStartScan(siteId, 'seo_home');
  const data = query.data;
  const checkedAt = data?.checkedAt ?? null;
  const cap = data ? capOf(data) : null;
  const check = (
    <Button size="sm" busy={start.isPending} disabled={running} onClick={() => start.mutate()}>
      {t('ai.check')}
    </Button>
  );

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('ai.title')}</h1>
          {checkedAt ? (
            <div className="sub">
              <span>
                {t('ai.checkedAt', { date: formatDate(checkedAt, i18n.language, timeZone) })}
              </span>
            </div>
          ) : null}
        </div>
        <div className="acts">{check}</div>
      </div>
      <p className="bodyM">{t('ai.intro')}</p>
      {start.isError ? <Notice kind="error">{scanError(start.error, t)}</Notice> : null}
      {running ? <Notice kind="info">{t('ai.running')}</Notice> : null}
      {query.isPending ? <Loading /> : null}
      {query.isError ? (
        <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
      ) : null}
      {data && checkedAt === null ? (
        <div className="empty">
          <b>{t('ai.none')}</b>
          <span>{t('ai.noneText')}</span>
          {running ? null : check}
        </div>
      ) : null}

      {data && checkedAt !== null && data.score !== null ? (
        <section className="card">
          <div className="ring">
            <ScoreRing score={data.score} label={t('ai.scoreLabel', { score: data.score })} />
            <div>
              <div className="h3s">{t(`ai.grade.${verdictOf(data.score)}`)}</div>
              <div className="cap">
                {t('ai.scoreOf', { score: data.score })} {t(`ai.verdict.${verdictOf(data.score)}`)}
                {cap ? ` ${t(`ai.capped.${cap.key}`, { cap: cap.cap })}` : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {data && checkedAt !== null ? (
        <section className="card" aria-labelledby={checksId}>
          <h2 id={checksId}>{t('ai.checksTitle')}</h2>
          <ul className="checks">
            {data.checks.map((c) => (
              <li key={c.key}>
                <span className={`badge ${TONE[c.status]} nodot`}>
                  {t(`ai.status.${c.status}`)}
                </span>
                <div>
                  <b>{t(`ai.checks.${c.key}.title`)}</b>
                  <span>
                    {c.status === 'unknown'
                      ? t('ai.unknownText')
                      : t(`ai.checks.${c.key}.${TEXT[c.status]}`, c.detail)}
                    {c.status === 'warn' || c.status === 'bad' ? (
                      <>
                        {' '}
                        <span className="todo">{t('ai.todo')}</span>{' '}
                        {t(`ai.checks.${c.key}.todo`, c.detail)}
                      </>
                    ) : null}
                  </span>
                </div>
                <span className="w">
                  <span className="sr-only">{t('ai.weight')} </span>
                  {c.weight}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data && checkedAt !== null ? (
        <section className="card" aria-labelledby={botsId}>
          <h2 id={botsId}>
            {t('ai.botsTitle')} <small>{t('ai.botsIntro')}</small>
          </h2>
          <div className="bots">
            {(['search', 'assistant', 'training'] as const).map((purpose) => (
              <div key={purpose}>
                <h3>{t(`ai.purpose.${purpose}.title`)}</h3>
                <p>{t(`ai.purpose.${purpose}.text`)}</p>
                <ul>
                  {data.bots
                    .filter((b) => b.purpose === purpose)
                    .map((b) => (
                      <li key={b.token}>
                        <span>
                          {b.token}
                          <small>{b.product}</small>
                        </span>
                        <span className={`badge ${botTone(b)} nodot`}>
                          {b.allowed === null
                            ? t('ai.bot.unknown')
                            : b.allowed
                              ? t('ai.bot.allowed')
                              : t('ai.bot.blocked')}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {data && checkedAt !== null ? (
        <section className="card" aria-labelledby={howId}>
          <h2 id={howId}>{t('ai.howTitle')}</h2>
          <p className="bodyM">{t('ai.how')}</p>
          <p className="bodyM">{t('ai.firewall')}</p>
          <Link className="more" to={`/sites/${siteId}/findings`}>
            {t('ai.toProblems')}
          </Link>
        </section>
      ) : null}
    </>
  );
}
